import { Bell, CalendarBlank, Checks, EnvelopeSimple, Files, GearSix, Receipt, Ticket, Timer, type Icon } from '@phosphor-icons/react';
import { useNotification, type Notification } from '../../../contexts/NotificationContext';
import { useIslandActions } from '../IslandProvider';
import { partitionNotifications, formatRelativeShort } from '../../../lib/island/notifications';
import { figgoSignal } from '../figgoSignal';
import { meldungsZiel } from '../../../lib/notifications/meldungsZiel';
import { FARBE } from '../pille-stil';

/** Module mit Icon und Farbe (Handoff Pille §5). */
const MODUL: Record<string, { icon: Icon; label: string; farbe: string }> = {
  tickets: { icon: Ticket, label: 'Tickets', farbe: FARBE.blau },
  rechnungen: { icon: Receipt, label: 'Rechnungen', farbe: FARBE.orange },
  emails: { icon: EnvelopeSimple, label: 'E-Mails', farbe: FARBE.indigo },
  dokumente: { icon: Files, label: 'Dokumente', farbe: FARBE.gruen },
  kalender: { icon: CalendarBlank, label: 'Kalender', farbe: FARBE.violett },
  zeiterfassung: { icon: Timer, label: 'Zeiterfassung', farbe: FARBE.orange },
  system: { icon: GearSix, label: 'System', farbe: FARBE.grau },
};

function Karte({ n, onOpen }: { n: Notification; onOpen: (n: Notification) => void }) {
  const m = MODUL[n.modul] ?? MODUL.system!;
  const IconKomp = m.icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(n)}
      aria-label={`${m.label}: ${n.nachricht}`}
      className="flex w-full shrink-0 cursor-pointer items-start text-left transition-colors hover:bg-white/[0.11]"
      style={{ gap: 12, padding: 12, borderRadius: 18, border: 'none', background: 'rgba(255,255,255,0.07)', boxShadow: 'inset 0 0 0 0.5px rgba(255,255,255,0.08)', color: '#fff', fontFamily: 'inherit' }}
    >
      <span className="flex shrink-0 items-center justify-center" style={{ width: 38, height: 38, borderRadius: 12, background: m.farbe }}>
        <IconKomp size={18} weight="fill" color="#fff" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
        <span className="flex items-baseline justify-between" style={{ gap: 8 }}>
          <span className="truncate uppercase" style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.45)' }}>{m.label}</span>
          <span className="shrink-0" style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{formatRelativeShort(n.created_at)}</span>
        </span>
        <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{n.nachricht}</span>
      </span>
    </button>
  );
}

/** Meldungen (Handoff Pille §5): ungelesene App-Benachrichtigungen als Karten, „Alle gelesen“. */
export function NotificationsPage() {
  const { notifications, unreadCount, markAsRead, markAllRead } = useNotification();
  const { collapse, onSearchNavigate } = useIslandActions();
  // Nur UNGELESENE zeigen — als gelesen markiert = verschwindet aus der Pille.
  const { unread } = partitionNotifications(notifications);

  function handleOpen(n: Notification) {
    markAsRead(n.id);
    // #605: direkt zum Datensatz (Dokument, Vorgang, Eingang …), nicht nur zur Übersicht
    const ziel = meldungsZiel(n);
    if (ziel && onSearchNavigate) {
      onSearchNavigate(ziel.pfad, ziel.id, ziel.typ);
      collapse();
    }
  }

  return (
    <div className="flex h-full flex-col text-white">
      {unreadCount > 0 && (
        <div className="flex shrink-0 items-center justify-between" style={{ padding: '0 16px 8px' }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'rgba(255,255,255,0.5)' }}>{unreadCount} ungelesen</span>
          <button type="button" onClick={() => { if (unreadCount > 1) figgoSignal('sweep', { force: true }); void markAllRead(); }} className="flex cursor-pointer items-center"
            style={{ height: 28, gap: 5, padding: '0 11px', borderRadius: 14, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 11, fontWeight: 700, fontFamily: 'inherit' }}>
            <Checks size={13} weight="bold" /> Alle gelesen
          </button>
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" style={{ gap: 8, padding: '0 16px 16px' }}>
        {unread.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center" style={{ gap: 8, color: 'rgba(255,255,255,0.4)' }}>
            <Bell size={28} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Keine neuen Benachrichtigungen</span>
          </div>
        ) : (
          unread.map((n) => <Karte key={n.id} n={n} onOpen={handleOpen} />)
        )}
      </div>
    </div>
  );
}
